import CloseFullscreenOutlinedIcon from '@mui/icons-material/CloseFullscreenOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import {Box, Grid, IconButton, Stack, Typography} from "@mui/material";
import {blue, grey} from "@mui/material/colors";
import {useLocation, useNavigate} from "react-router-dom";
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import {useAppContext} from "@/contexts/AppContext.tsx";
import {useMemo, useState} from 'react';
import {Path, PathItem, SidebarTypes} from "@/types/utils.ts";
import Icons from "@/features/Dashboard/Icons";

function Badge({show}: any) {
    return (
        <Box sx={{mt: 0.25, borderRadius: 1, width: 5, height: 5, bgcolor: blue[900], opacity: show ? 1 : 0}}/>
    )
}

function SidebarCollapsedMenu({children}: { children: PathItem[] }) {
    const navigate = useNavigate();
    const {uiVisual} = useAppContext();

    const block = {
        classic: {
            width: 180,
            position: 'absolute',
            top: 20,
            left: 25,
            bgcolor: "#fcfdff",
            boxShadow: '0 1px 1px 0 #001c244d, 1px 1px 1px 0 #001c2426, -1px 1px 1px 0 #001c2426',
            zIndex: 2,
            borderRadius: 0
        },
        modern: {
            width: 180,
            position: 'absolute',
            top: 24,
            left: 36,
            bgcolor: "#fcfdff",
            border: '3px solid white',
            boxShadow: '0px 4px 10px 0px #E0E0E04D',
            zIndex: 2,
            borderRadius: 2
        }
    }
    const text = {
        classic: {
            py: 1,
            px: 2,
            "&:hover": {
                bgcolor: grey[100]
            }
        },
        modern: {
            py: 1,
            px: 2,
            "&:hover": {
                bgcolor: "#f1f5ff"
            }
        }
    }

    return (
        <Box sx={block[uiVisual]}>
            <MenuList dense sx={{py: 0}}>
                {children?.map((item) => {
                    return (
                        <MenuItem disabled={item.disabled} sx={text[uiVisual]} key={item.name}
                                  onClick={() => item.slug && navigate(item.slug)}>
                            <Typography fontSize={13}>{item.name}</Typography>
                        </MenuItem>
                    )
                })}
            </MenuList>
        </Box>
    )
}

function SidebarMenu({sidebarExpanded, paths}: { sidebarExpanded: boolean, paths: Path[] }) {
    const navigate = useNavigate();
    const location = useLocation();
    const {uiVisual, theme} = useAppContext();

    const fullpath = useMemo(() => `${location.pathname}${location.hash}`, [location]);
    const current = useMemo(() => {
        let path = `${location.pathname}${location.hash}`

        // perform merge children paths of networking features to the same of networks key
        if (['networks', 'secgroups', 'reserves', 'sshkeys']?.filter((key) => path.match(key))?.length !== 0) {
            path = 'networks';
        }
        // console.log(pathname);

        return path;
    }, [location.hash, location.pathname]);
    const active = useMemo(
        () => paths?.find((item) => {
            // console.log(item.key, current);

            const rgex = current?.trim()?.match(item.key);
            // console.log(current, item.key, rgex);

            // console.log(rgex);

            return typeof rgex?.[0] !== "undefined"
        }),
        [current, paths]);

    const [expand, setExpand] = useState<string>(sidebarExpanded && active?.name || '');

    // const active = useMemo(() => params?.['*'], [params]);
    const list = {
        classic: {
            px: 1
        },
        modern: {
            px: sidebarExpanded ? 2 : 0.5
        }
    };

    return (
        <>
            <MenuList dense sx={list[uiVisual]}>
                {paths?.map((path, index) => {
                    const isExpanded = expand === path.name;
                    const children = path?.items || [];
                    const Icon = path.icon;

                    const key = `${path.name}.${index}`;
                    const isActive = fullpath === path.slug;
                    const items = children.filter((item) => !item.disabled);
                    const isChildrenExpanded = children?.filter((item) => {
                        // console.log(active);
                        // console.log(fullpath, item.slug, item.key);

                        // if (active && item.slug.match(active.key)) {
                        //     console.log(item.slug, active.key)
                        // }

                        return fullpath === item.slug || fullpath === item.key;
                    })?.length !== 0;

                    // console.log(fullpath, isChildrenExpanded)
                    const parent = {
                        classic: {
                            position: 'relative'
                        },
                        modern: {
                            position: 'relative',
                            "&:before": sidebarExpanded && {
                                content: '""',
                                width: 2,
                                height: 41 * items.length,
                                display: isExpanded ? 'block' : 'none',
                                position: 'absolute',
                                top: 43,
                                left: 22,
                                bgcolor: '#E1E7FF',
                            } || '',
                            "&:hover": {
                                bgcolor: '#e5edff'
                            }
                        }
                    }
                    const block = {
                        classic: {
                            py: 1,
                            px: 1.5,
                            // mb: (sidebarExpanded && isExpanded) ? 0 : 1,
                            bgcolor: isActive ? grey[200] : (isExpanded && !sidebarExpanded) ? grey[200] : '',
                            // boxShadow: isActive ? '0px 4px 10px 0px #E0E0E04D' : '',
                            borderRadius: 0
                        },
                        modern: {
                            py: 1,
                            px: 1.5,
                            mb: (sidebarExpanded && isExpanded) ? 0 : 1,
                            bgcolor: isActive ? (theme === 'dark' ? '#e5edff' : '#f1f5ff') : (isExpanded && !sidebarExpanded) ? '#EEF1FF' : '',
                            border: theme === 'dark' ? '3px solid transparent' : isActive ? '3px solid white' : '3px solid transparent',
                            boxShadow: isActive ? '0px 4px 10px 0px #E0E0E04D' : '',
                            borderRadius: 1
                        }
                    }
                    const text = {
                        classic: {
                            display: 'block',
                            mr: 0.25,
                            fontSize: 14,
                            color: (isActive || isChildrenExpanded) ? grey[900] : grey[700],
                            fontWeight: (isActive || isChildrenExpanded) ? 500 : 400
                        },
                        modern: {
                            display: 'block',
                            mr: 0.25,
                            fontSize: 14,
                            color: (isActive || isChildrenExpanded) ? blue[900] : grey[800],
                            fontWeight: 500
                        }
                    }

                    return (
                        <Box key={key}
                             onMouseOver={() => !sidebarExpanded && setExpand(path.name)}
                             onMouseLeave={() => !sidebarExpanded && setExpand('')}
                             sx={parent[uiVisual]}>
                            <MenuItem disabled={path.disabled}
                                      onClick={() => path.items?.length !== 0 ? setExpand(path.name) : navigate(path.slug)}
                                      disableTouchRipple
                                      disableRipple
                                      sx={block[uiVisual]}>
                                {Icon && (
                                    <ListItemIcon>
                                        <Icon sx={{...text[uiVisual]}}/>
                                    </ListItemIcon>
                                )}
                                {sidebarExpanded && (
                                    <ListItemText>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Stack direction="row">
                                                <Typography sx={text[uiVisual]}>
                                                    {path.name}
                                                </Typography>
                                                <Badge show={path.beta}/>
                                            </Stack>
                                            {children.length !== 0 && (
                                                <Box>
                                                    {isExpanded ? (
                                                        <KeyboardArrowDownRoundedIcon
                                                            sx={{...text[uiVisual], fontSize: 20}}/>
                                                    ) : (
                                                        <KeyboardArrowRightRoundedIcon
                                                            sx={{...text[uiVisual], fontSize: 20}}/>
                                                    )}
                                                </Box>
                                            )}
                                        </Stack>
                                    </ListItemText>
                                )}
                            </MenuItem>
                            {!sidebarExpanded && isExpanded && children.length !== 0 && (
                                <SidebarCollapsedMenu children={children}/>
                            )}
                            {isExpanded && sidebarExpanded && children.map((item, index) => {
                                const key = `${item.name}.${index}`;
                                const isActive = fullpath === item.slug || fullpath === item.key;
                                const isLatest = index === children.length - 1;
                                // console.log([location.pathname, item.slug], [pathname, item.key]);

                                // console.log(item, isActive);
                                // console.log(fullpath, item.slug, isActive, isChildrenExpanded)

                                const block = {
                                    classic: {
                                        mb: isLatest && 1 || 0,
                                        py: 1,
                                        pl: 5.75,
                                        bgcolor: isActive ? grey[100] : '',
                                        fontWeight: isActive ? 500 : 400,
                                        borderRadius: 0,
                                        transition: 'padding linear 0.1s',
                                        "&:hover": {
                                            bgcolor: grey[100]
                                        }
                                    },
                                    modern: {
                                        mb: isLatest && 1 || 0,
                                        py: 1,
                                        pl: 5.75,
                                        bgcolor: isActive ? (theme === 'dark' ? '#e5edff' : '#f1f5ff') : '',
                                        border: isActive ? (theme === 'dark' ? '3px solid transparent' : '3px solid white') : '3px solid transparent',
                                        boxShadow: isActive ? (theme === 'dark' ? '' : '0px 4px 10px 0px #E0E0E04D') : '',
                                        borderRadius: 1,
                                        transition: 'padding linear 0.1s',
                                        "&:hover": {
                                            bgcolor: '#EEF1FF',
                                            pl: 5.25,
                                        },
                                        "&:before": isActive && {
                                            content: '""',
                                            width: 2,
                                            height: 24,
                                            bgcolor: blue[900],
                                            position: 'absolute',
                                            left: 20
                                        } || '-'
                                    }
                                }
                                const text = {
                                    classic: {
                                        display: 'block',
                                        mr: 0.25,
                                        fontSize: 13,
                                        fontWeight: isActive ? 500 : 400,
                                        color: isActive ? grey[900] : grey[600]
                                    },
                                    modern: {
                                        display: 'block',
                                        mr: 0.25,
                                        fontSize: 13,
                                        fontWeight: 400,
                                        color: isActive ? blue[900] : grey[800]
                                    }
                                }

                                return (
                                    <MenuItem key={key}
                                              disabled={item.disabled}
                                              onClick={() => item.slug && navigate(item.slug)}
                                              sx={block[uiVisual]}>
                                        <ListItemText>
                                            <Stack direction="row">
                                                <Typography sx={text[uiVisual]}>
                                                    {item.name}
                                                </Typography>
                                                <Badge show={item.beta}/>
                                            </Stack>
                                        </ListItemText>
                                    </MenuItem>
                                )
                            })}
                        </Box>
                    )
                })}
            </MenuList>
        </>
    )
}

export default function MainSidebar({name, provider, paths}: SidebarTypes) {
    const {uiVisual, theme, sidebar, sidebarWidth, toggleSidebar} = useAppContext();

    const width = useMemo(() => sidebar ? sidebarWidth : 60, [sidebar, sidebarWidth]);
    const iconKey = provider?.split(".").join("") || '';
    const Icon = Icons[iconKey];

    const block = {
        classic: {
            boxShadow: theme === 'dark' ? 'unset' : '0 1px 1px 0 rgba(0, 28, 36, .3), 1px 1px 1px 0 rgba(0, 28, 36, .15), -1px 1px 1px 0 rgba(0, 28, 36, .15)',
            borderRight: theme === 'dark' ? '1px solid #e0e0e0' : 'unset',
            bgcolor: theme === 'dark' ? 'white' : ''
        },
        modern: {
            bgcolor: theme === 'dark' ? '#f8fdff' : '#f9faff'
        }
    };
    const heading = {
        classic: {
            bgcolor: theme === 'dark' ? grey[200] : '',
            borderBottom: '1px solid #e0e0e0'
        },
        modern: {
            bgcolor: theme === 'dark' ? '#f8fdff' : '',
            borderBottom: theme === 'dark' ? '1px solid #e5edff' : '1px solid #ECF4FF'
        }
    };
    const icon = {
        classic: {
            filter: theme === 'dark' ? 'invert(1)' : 'invert(0)'
        },
        modern: {
            filter: theme === 'dark' ? 'invert(1)' : 'invert(0)'
        }
    }

    return (
        <Grid item width={width} zIndex={1} minHeight="calc(100vh - 100px)" sx={block[uiVisual]}>
            <Box p={1} height={sidebar ? 50 : provider ? 100 : 50} bgcolor={heading[uiVisual].bgcolor}
                 borderBottom={heading[uiVisual].borderBottom}>
                {sidebar ? (
                    <Stack direction="row" justifyContent="center">
                        {Icon && <Icon my="auto" sx={icon[uiVisual]}/>}
                        <Box width="100%" my="auto" px={1}>
                            <Typography fontSize={14} fontWeight={500}>{name}</Typography>
                        </Box>
                        <Box>
                            <IconButton aria-label="collapse" onClick={() => toggleSidebar(!sidebar)}>
                                <CloseFullscreenOutlinedIcon sx={{fontSize: 18, color: '#213547'}}/>
                            </IconButton>
                        </Box>
                    </Stack>
                ) : (
                    <Stack direction="column" justifyContent="center">
                        <Box mx="auto" mb={1}>
                            <IconButton aria-label="collapse" onClick={() => toggleSidebar(!sidebar)}>
                                <OpenInFullOutlinedIcon sx={{fontSize: 18, color: '#213547'}}/>
                            </IconButton>
                        </Box>
                        {Icon && (
                            <Stack width={34} height={50} mx="auto" direction="row" borderTop="1px solid #ECF4FF">
                                <Icon width={34} sx={icon[uiVisual]}/>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>
            <SidebarMenu sidebarExpanded={sidebar} paths={paths}/>
        </Grid>
    )
}